import { Collapse, createTheme, ThemeProvider } from "@mui/material";
import Paper from "@mui/material/Paper";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import { Cup } from "assets/images";
import Colors from "constants/Colors";
import { GameRecord } from "domain/models/GameRecord";
import { useDeviceSize } from "hooks/useIsMobile";
import React, { CSSProperties, FC } from "react";
import { useTranslation } from "react-i18next";
import { TimeUtils } from "utils/TimeUtils";
import { LeaderBoardType } from "../../models/LeaderBoardType";
import { PodiumTableCell } from "../LeaderBoardPodiumTableCell/LeaderBoardPodiumTableCell";
import "./LeaderBoardResultList.scss";

const listHeaderTheme = (bgColor: string) =>
  createTheme({
    components: {
      MuiTableCell: {
        styleOverrides: {
          root: {
            padding: "1vh",
            borderTopColor: "#fff",
            borderWidth: "0.5vh",
            borderColor: "transparent",
            fontFamily: "ReportSchoolRegular",
            fontSize: "3vh",
            whiteSpace: "normal",
            wordBreak: "break-word",
          },
        },
      },
      MuiTableRow: {
        styleOverrides: {
          root: {
            whiteSpace: "normal",
            wordBreak: "break-word",
          },
        },
      },
    },
  });

const listTheme = createTheme({
  components: {
    MuiTable: {
      styleOverrides: {
        root: {
          border: "none",
          borderCollapse: "separate",
        },
      },
    },
    MuiTableCell: {
      styleOverrides: {
        root: {
          borderBottom: "none",
          padding: "1vh",
          backgroundColor: "transparent",
          fontFamily: "ReportSchoolRegular",
          fontSize: "3vh",
        },
      },
    },
    MuiTableContainer: {
      styleOverrides: {
        root: {
          boxShadow: "none",
        },
      },
    },
    MuiTableRow: {
      styleOverrides: {
        root: {
          borderRadius: "100px",
        },
      },
    },
  },
});

interface Props {
  tableHeight: number;
  tableWidth: number;
  leaderBoardType: LeaderBoardType;
  data: GameRecord[];
  lastweeksWinner?: GameRecord | null;
}

export const listRowBorderRadius = "2vh";

const leftSideCellStyle: (isMobile: boolean) => CSSProperties = (
  isMobile: boolean
) => {
  return {
    borderTopLeftRadius: listRowBorderRadius,
    borderBottomLeftRadius: isMobile ? 0 : listRowBorderRadius,
    paddingRight: isMobile ? 0 : 20
  };
};

const rightSideCellStyle: (isMobile: boolean) => CSSProperties = (
  isMobile: boolean
) => {
  return {
    borderBottomRightRadius: isMobile ? 0 : listRowBorderRadius,
    borderTopRightRadius: listRowBorderRadius,
  };
};

const headerCellStyle = (
  color: string,
  hasRightBorder?: boolean
): CSSProperties => {
  let rightBorderStyle: CSSProperties | undefined = undefined;
  if (!hasRightBorder) {
    rightBorderStyle = { borderRight: "0px" };
  }

  return {
    borderBottom: listRowBorderRadius,
    border: "2px solid black",
    borderColor: color,
    borderTop: "0px",
    borderLeft: "0px",
    ...rightBorderStyle,
  };
};

const timeCellStyle: CSSProperties = {
  fontSize: "4vh",
  wordBreak: "initial",
};

export const LeaderBoardResultList: FC<Props> = (props) => {
  const { t } = useTranslation();

  const { isMobile } = useDeviceSize();

  const getStyleForHeader = () => {
    switch (props.leaderBoardType) {
      case LeaderBoardType.BASIC:
        return Colors.brand.yellowDark;
      case LeaderBoardType.ADVANCED:
        return Colors.brand.purpleDark;
      case LeaderBoardType.EXPERT:
        return Colors.brand.darkGreen;
      case LeaderBoardType.GENIUS:
        return Colors.brand.redDark;
    }
  };

  const getTableRow = (row: GameRecord, index: number) => {
    return (
      <TableRow
        className={`LeaderBoardTableRow ${
          index % 2 === 1 ? "LeaderBoardColoredTableRow" : ""
        }`}
        key={row.rank ?? index}
        sx={{ "&:last-child td, &:last-child th": { border: 0 } }}
      >
        <PodiumTableCell
          position={index + 1}
          style={leftSideCellStyle(isMobile)}
        />
        <TableCell align="left">{row.roomName}</TableCell>
        <TableCell align="left">{row.schoolName}</TableCell>
        <TableCell align="left">{row.district}</TableCell>
        <TableCell align="center">{row.state}</TableCell>
        <TableCell
          align="right"
          style={{
            ...rightSideCellStyle(isMobile),
            ...timeCellStyle,
          }}
        >
          {TimeUtils.formatMillisecondsToShortSec(row.usedTimeMs)}
        </TableCell>
      </TableRow>
    );
  };

  const getMobileTableRow = (row: GameRecord, index: number) => {
    return (
      <>
        <TableRow
          className={`LeaderBoardTableRow ${
            index % 2 === 1 ? "LeaderBoardColoredTableRow" : ""
          }`}
          key={index}
          sx={{ "&:last-child td, &:last-child th": { border: 0 } }}
        >
          <PodiumTableCell
            position={index + 1}
            style={{
              ...leftSideCellStyle(isMobile),
              verticalAlign: "top",
            }}
          />
          <TableCell className="LeaderBoardTitleCell" align="left" colSpan={3}>
            {row.roomName}
          </TableCell>
          <TableCell
            align="right"
            style={{
              ...rightSideCellStyle(isMobile),
              ...timeCellStyle,
            }}
          >
            {TimeUtils.formatMillisecondsToShortSec(row.usedTimeMs)}
          </TableCell>
        </TableRow>
        <TableRow key={row.roomName}>
          <TableCell
            style={{
              paddingBottom: 0,
              paddingTop: 0,
              borderBottomLeftRadius: listRowBorderRadius,
              borderBottomRightRadius: listRowBorderRadius,
              ...(index % 2 == 1
                ? {
                    backgroundColor: Colors.brand.grayLow,
                  }
                : {}),
            }}
            colSpan={5}
          >
            <Collapse in={true} timeout="auto" unmountOnExit>
              <div className="LeaderBoardCollapsibleContent">
                <div className="LeaderBoardCollapsibleRow">
                  <div className="LeaderBoardCollapsibleValue">
                    {`${row.schoolName}, ${row.district}, ${row.state}`}
                  </div>
                </div>
              </div>
            </Collapse>
          </TableCell>
        </TableRow>
      </>
    );
  };

  const getTableHeader = () => {
    return (
      <>
        <TableRow>
          <TableCell
            colSpan={6}
            style={{
              borderTopLeftRadius: listRowBorderRadius,
              borderTopRightRadius: listRowBorderRadius,
              backgroundColor: getStyleForHeader(),
              borderBottom: "none",
            }}
          >
            <div className="LeaderBoardHeaderTitleContainer">
              {t("lastweeksWinner").toUpperCase()}
            </div>
          </TableCell>
        </TableRow>
        <TableRow>
          <TableCell
            align="right"
            style={{
              border: "2px solid black",
              borderColor: getStyleForHeader(),
              borderBottomLeftRadius: listRowBorderRadius,
              borderTop: "0px",
              borderRight: "0px",
            }}
          >
            <div className="LeaderBoardHeaderCupContainer">
              <img src={Cup} className="LeaderBoardHeaderCup" alt="Trophy" />
            </div>
          </TableCell>
          <TableCell align="left" style={headerCellStyle(getStyleForHeader())}>
            <div className="LeaderBoardHeaderTitleItem">
              {props.lastweeksWinner?.roomName}
            </div>
          </TableCell>
          <TableCell align="left" style={headerCellStyle(getStyleForHeader())}>
            <div className="LeaderBoardHeaderTitleItem">
              {props.lastweeksWinner?.schoolName}
            </div>
          </TableCell>
          <TableCell align="left" style={headerCellStyle(getStyleForHeader())}>
            <div className="LeaderBoardHeaderTitleItem">
              {props.lastweeksWinner?.district}
            </div>
          </TableCell>
          <TableCell
            align="center"
            style={{ ...headerCellStyle(getStyleForHeader()), padding: "0vh" }}
          >
            <div className="LeaderBoardHeaderTitleItem">
              {props.lastweeksWinner?.state}
            </div>
          </TableCell>
          <TableCell
            align="right"
            style={{
              ...headerCellStyle(getStyleForHeader(), true),
              borderBottomRightRadius: listRowBorderRadius,
              ...timeCellStyle,
            }}
          >
            <div className="LeaderBoardHeaderTitleItem">
              {TimeUtils.formatMillisecondsToShortSec(
                props.lastweeksWinner?.usedTimeMs ?? 0
              )}
            </div>
          </TableCell>
        </TableRow>
      </>
    );
  };

  const getMobileTableHeader = () => {
    return (
      <>
        <TableRow>
          <TableCell
            colSpan={6}
            style={{
              borderTopLeftRadius: listRowBorderRadius,
              borderTopRightRadius: listRowBorderRadius,
              backgroundColor: getStyleForHeader(),
              borderBottom: "none",
            }}
          >
            <div className="LeaderBoardHeaderTitleContainer">
              {t("lastweeksWinner").toUpperCase()}
            </div>
          </TableCell>
        </TableRow>
        <TableRow>
          <TableCell
            align="right"
            style={{
              borderLeft: `2px solid ${getStyleForHeader()}`,
              borderBottom: "none",
            }}
          >
            <div className="LeaderBoardHeaderCupContainer">
              <img src={Cup} className="LeaderBoardHeaderCup" alt="Trophy" />
            </div>
          </TableCell>
          <TableCell align="left" colSpan={3}>
            <div className="LeaderBoardHeaderTitleItem">
              {props.lastweeksWinner?.roomName}
            </div>
          </TableCell>
          <TableCell
            align="right"
            style={{
              ...timeCellStyle,
              borderRight: `2px solid ${getStyleForHeader()}`,
              borderBottom: "none",
            }}
          >
            <div className="LeaderBoardHeaderTitleItem">
              {TimeUtils.formatMillisecondsToShortSec(
                props.lastweeksWinner?.usedTimeMs ?? 0
              )}
            </div>
          </TableCell>
        </TableRow>
        <TableRow>
          <TableCell
            style={{
              ...headerCellStyle(getStyleForHeader()),
              borderLeft: `2px solid ${getStyleForHeader()}`,
              borderRight: `2px solid ${getStyleForHeader()}`,
              borderBottomLeftRadius: listRowBorderRadius,
              borderBottomRightRadius: listRowBorderRadius,
            }}
            colSpan={5}
          >
            <Collapse in={true} timeout="auto" unmountOnExit>
              <div className="LeaderBoardCollapsibleContent">
                <div className="LeaderBoardCollapsibleRow">
                  <div className="LeaderBoardCollapsibleValue">
                    {`${props.lastweeksWinner?.schoolName}, ${
                      props.lastweeksWinner?.district
                    }, ${props.lastweeksWinner?.state ?? ""}`}
                  </div>
                </div>
              </div>
            </Collapse>
          </TableCell>
        </TableRow>
      </>
    );
  };

  return (
    <ThemeProvider theme={listTheme}>
      <TableContainer
        component={Paper}
        sx={
          isMobile
            ? {
                width: props.tableWidth,
                maxWidth: props.tableWidth,
              }
            : {
                height: props.tableHeight,
              }
        }
      >
        <Table
          sx={{
            ...(isMobile ? {} : { minWidth: 650 }),
            height: "max-content",
          }}
          aria-label="Capit Sound Challange results"
        >
          {isMobile && (
            <colgroup>
              <col style={{ width: "25%" }} />
              <col style={{ width: "55%" }} />
              <col style={{ width: "20%" }} />
            </colgroup>
          )}
          <ThemeProvider theme={listHeaderTheme(getStyleForHeader())}>
            {props.lastweeksWinner && (
              <TableHead>
                {isMobile ? getMobileTableHeader() : getTableHeader()}
              </TableHead>
            )}
          </ThemeProvider>
          <TableBody>
            {props.data.map((row, index) => (
              <React.Fragment key={row.rank ?? index}>
                {isMobile
                  ? getMobileTableRow(row, index)
                  : getTableRow(row, index)}
              </React.Fragment>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </ThemeProvider>
  );
};
